// src/pages/invoicing/edit-ti-items.js
import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import { Link, useParams } from "react-router-dom";
import Select from "react-select";
import API_BASE_URL from "../../config";
import { Button } from "react-bootstrap";

const EditTIItems = () => {
  const { id } = useParams(); // TI ID
  const [lead, setLead] = useState(null);            // snapshot built from TI header (to_* fields)
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [billing, setBilling] = useState(null);
  const [status, setStatus] = useState("Due");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [missingLeadFields, setMissingLeadFields] = useState({ state: false });
  const [tiHeader, setTiHeader] = useState(null);

  const [lineItems, setLineItems] = useState([
    { sac: "998314", title: "", gst: 18, amount: "" },
  ]);

  const buildLeadFromTIHeader = (hdr) => ({
    id: hdr.lead_id ?? null, // may be null (standalone)
    company: hdr.to_company || "",
    address: hdr.to_address || "",
    city: hdr.to_city || "",
    state: hdr.to_state || "",
    pincode: hdr.to_pincode || "",
    gst_number: hdr.to_gstin || "",
  });

  useEffect(() => {
    document.title = "Edit Tax Invoice - Items | Webyappy";

    // brands
    fetch(`${API_BASE_URL}/invoicing/get_brands`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => d.status === "success" && setBrands(d.brands || []))
      .catch(() => {});

    // billing address
    fetch(`${API_BASE_URL}/invoicing/get_billing_address`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => d.status === "success" && setBilling(d.billing_address))
      .catch(() => {});

    // TI header
    fetch(`${API_BASE_URL}/invoicing/get_ti/${id}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.status !== "success") {
          alert("Tax Invoice not found");
          return;
        }
        const hdr = data.invoice;
        setTiHeader(hdr);
        setStatus(hdr.status || "Due");

        const snap = buildLeadFromTIHeader(hdr);
        setLead(snap);
        setMissingLeadFields({
          state: !snap.state || String(snap.state).trim() === "",
        });
      })
      .catch((e) => console.error(e));

    // TI items
    fetch(`${API_BASE_URL}/invoicing/get_ti_items/${id}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === "success") {
          const mapped =
            (data.items || []).map((it) => ({
              sac: String(it.sac_code ?? "998314"),
              title: String(it.title ?? ""),
              gst: Number(it.gst_percent ?? 18),
              amount:
                it.amount === null || it.amount === undefined
                  ? ""
                  : String(it.amount),
            })) || [];
          setLineItems(mapped.length ? mapped : [{ sac: "998314", title: "", gst: 18, amount: "" }]);
        }
      })
      .catch((e) => console.error(e));
  }, [API_BASE_URL, id]);

  // preselect brand after both header & brands present
  useEffect(() => {
    if (!tiHeader || !brands.length) return;
    const found = brands.find((b) => String(b.id) === String(tiHeader.brand_id));
    setSelectedBrand(
      found
        ? { value: found.id, label: found.brand_name, logo: found.logo, color: found.color_code }
        : null
    );
  }, [brands, tiHeader]);

  // handlers
  const handleLineItemChange = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };
  const addLineItem = () => {
    setLineItems([...lineItems, { sac: "998314", title: "", gst: 18, amount: "" }]);
  };
  const removeLineItem = (index) => {
    const updated = [...lineItems];
    updated.splice(index, 1);
    setLineItems(updated);
  };

  // totals
  const calculateSubtotal = () =>
    lineItems.reduce((sum, item) => {
      const amt = parseFloat(item.amount || 0);
      return sum + (isNaN(amt) ? 0 : amt);
    }, 0);

  const calculateTotalGST = () =>
    lineItems.reduce((sum, item) => {
      const amt = parseFloat(item.amount || 0);
      const gst = parseFloat(item.gst || 0);
      return sum + (amt * gst) / 100;
    }, 0);

  const calculateGrandTotal = () => calculateSubtotal() + calculateTotalGST();

  // submit update
  const handleSubmitInvoice = async () => {
    if (!lead) return;
    if (missingLeadFields.state) return;

    if (!selectedBrand) {
      alert("Please select a brand before submitting.");
      return;
    }

    // validate rows
    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      if (!item.title || item.title.trim() === "") {
        alert(`Please enter title for item ${i + 1}`);
        return;
      }
      if (!item.amount || isNaN(item.amount) || parseFloat(item.amount) <= 0) {
        alert(`Please enter valid amount for item ${i + 1}`);
        return;
      }
      if (item.gst === "" || isNaN(item.gst)) {
        alert(`Please enter GST% for item ${i + 1}`);
        return;
      }
      if (!item.sac || item.sac.trim() === "") {
        alert(`Please enter SAC code for item ${i + 1}`);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // 1) Update TI header
      const updHeader = await fetch(`${API_BASE_URL}/invoicing/update_tax_invoice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ti_id: id,
          brand_id: selectedBrand?.value || null,
          status, // Paid/Due
          // keep the bill-to snapshot editable based on current lead snapshot
          to_company: lead.company,
          to_address: lead.address,
          to_city: lead.city,
          to_state: lead.state,
          to_pincode: lead.pincode,
          to_gstin: lead.gst_number ?? "",
        }),
      });
      const updData = await updHeader.json();
      if (updData.status !== "success") {
        alert("Failed to update tax invoice header.");
        setIsSubmitting(false);
        return;
      }

      // 2) Replace TI items
      const itemsToSave = lineItems.map((item) => ({
        ti_id: id,
        title: item.title,
        sac_code: item.sac,
        gst_percent: Number(item.gst),
        amount: parseFloat(item.amount),
      }));

      const liRes = await fetch(`${API_BASE_URL}/invoicing/replace_tax_line_items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(itemsToSave),
      });
      const liData = await liRes.json();
      if (liData.status !== "success") {
        alert("Failed to save items.");
        setIsSubmitting(false);
        return;
      }

      // 3) Totals (CGST+SGST or IGST)
      const subtotal = +calculateSubtotal().toFixed(2);
      const totalGST = +calculateTotalGST().toFixed(2);

      let cgst = 0,
        sgst = 0,
        igst = 0;
      if (
        billing?.state?.toLowerCase().trim() ===
        (lead?.state || "").toLowerCase().trim()
      ) {
        cgst = +(totalGST / 2).toFixed(2);
        sgst = +(totalGST / 2).toFixed(2);
      } else {
        igst = +totalGST.toFixed(2);
      }

      const grand_total = +(subtotal + totalGST).toFixed(2);

      await fetch(`${API_BASE_URL}/invoicing/update_tax_totals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ti_id: id,
          subtotal,
          cgst,
          sgst,
          igst,
          grand_total,
        }),
      });

      // 4) Redirect to TI view
      window.location.href = `/pages/invoicing/ti-view/${id}`;
    } catch (err) {
      console.error("Error updating tax invoice:", err);
      alert("Something went wrong.");
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div className="container-fluid px-3 py-3">
          <div className="row">
            {/* LEFT: Company snapshot */}
            {lead ? (
              <div className="col-12 col-lg-4 mb-4">
                <h6 className="font-weight-bold text-dark mb-3">Company Details:</h6>
                <div className="card card-custom shadow-sm px-4 py-4 h-100">
                  <div className="mb-2">
                    <h5 className="text-dark font-weight-bold mb-1" style={{ fontSize: "15px" }}>
                      {lead.company || "—"}
                    </h5>
                  </div>
                  <div className="text-dark mb-3" style={{ fontSize: "13px" }}>
                    {lead.address || lead.city || lead.state || lead.pincode ? (
                      <>
                        <div>{lead.address || "—"}</div>
                        <div>
                          {lead.city || "—"}
                          {lead.city && (lead.state || lead.pincode) ? ", " : ""}
                          {lead.state || ""}
                          {lead.state && lead.pincode ? " - " : ""}
                          {lead.pincode || ""}
                        </div>
                      </>
                    ) : (
                      <div>—</div>
                    )}
                  </div>
                  <div className="text-dark" style={{ fontSize: "13px" }}>
                    <strong>GSTIN:</strong> {lead.gst_number || "—"}
                  </div>
                  {missingLeadFields.state && (
                    <div className="alert alert-danger mt-3 small mb-0">
                      <i className="fas fa-exclamation-triangle mr-2"></i>
                      Lead <strong>State</strong> is missing. GST calculation cannot be determined.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="col-12 col-lg-4 mb-4">
                <div className="card card-custom shadow-sm p-4 text-center text-muted">
                  No details found.
                </div>
              </div>
            )}

            {/* RIGHT: Items + Brand + Status + Submit */}
            <div className="col-12 col-lg-8 mb-4">
              <h6 className="font-weight-bold text-dark mb-3">Tax Invoice Item Entry</h6>
              <div className="card card-custom shadow-sm p-4">
                <div className="form-row row">
                  <div className="form-group col-12 col-lg-8">
                    <label className="small">
                      Brand <span className="text-danger">*</span>
                    </label>
                    <Select
                      className={`basic-single shadow-sm ${!selectedBrand && isSubmitting ? "border border-danger" : ""}`}
                      classNamePrefix="select"
                      options={brands.map((b) => ({
                        label: b.brand_name,
                        value: b.id,
                        logo: b.logo,
                        color: b.color_code,
                      }))}
                      placeholder="Select Brand"
                      isClearable
                      value={selectedBrand}
                      onChange={(s) => setSelectedBrand(s)}
                      formatOptionLabel={({ label, logo, color }) => (
                        <div className="d-flex align-items-center">
                          <div
                            className="border mr-2 d-flex align-items-center justify-content-center"
                            style={{ width: "40px", height: "40px", borderRadius: "4px", overflow: "hidden", marginRight: "10px" }}
                          >
                            <img
                              src={
                                logo?.startsWith("http")
                                  ? logo
                                  : `${API_BASE_URL.replace("/index.php", "")}/${logo}`
                              }
                              alt={label}
                              style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", padding: "4px" }}
                            />
                          </div>
                          <span style={{ color, fontSize: "14px", fontWeight: "Bold" }}>{label}</span>
                        </div>
                      )}
                    />
                  </div>

                  <div className="form-group col-12 col-lg-4">
                    <label className="small">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control shadow-sm"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="Due">Due</option>
                      <option value="Paid">Paid</option>
                    </select>
                  </div>
                </div>

                {lineItems.map((item, index) => (
                  <div className="row mb-3 mt-3" key={index}>
                    <div className="col-sm-6 col-md-1 form-group mb-2">
                      <label className="small font-weight-bold">Sr. No.</label>
                      <input type="text" className="form-control" value={index + 1} readOnly />
                    </div>
                    <div className="col-sm-6 col-md-2 form-group mb-2">
                      <label className="small font-weight-bold">SAC</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.sac}
                        onChange={(e) => handleLineItemChange(index, "sac", e.target.value)}
                      />
                    </div>
                    <div className="col-md-5 form-group mb-1">
                      <label className="small font-weight-bold">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Service Title"
                        value={item.title}
                        onChange={(e) => handleLineItemChange(index, "title", e.target.value)}
                      />
                    </div>
                    <div className="col-sm-6 col-md-2 form-group mb-2">
                      <label className="small font-weight-bold">GST%</label>
                      <input
                        type="number"
                        className="form-control"
                        value={item.gst}
                        onChange={(e) => handleLineItemChange(index, "gst", e.target.value)}
                      />
                    </div>
                    <div className="col-sm-6 col-md-2 form-group mb-2">
                      <label className="small font-weight-bold">Amount</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          // allow digits + optional decimal up to 2 places
                          if (/^\d*\.?\d{0,2}$/.test(value)) {
                            handleLineItemChange(index, "amount", value);
                          }
                        }}
                      />
                    </div>

                    <div className="col-md-1 d-flex align-items-end" style={{ paddingBottom: "12px" }}>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}

                <div className="text-right mb-4">
                  <button className="btn btn-sm btn-outline-primary" onClick={addLineItem}>
                    <i className="fas fa-plus-circle mr-1"></i> Add Item
                  </button>
                </div>

                <div className="mt-4 border-top pt-4 text-right">
                  <div>
                    <strong>Subtotal:</strong> ₹{calculateSubtotal().toFixed(2)}
                  </div>
                  <div>
                    <strong>GST:</strong> ₹{calculateTotalGST().toFixed(2)}
                  </div>
                  <div className="font-weight-bold">
                    <strong>Grand Total:</strong> ₹{calculateGrandTotal().toFixed(2)}
                  </div>
                </div>

                <div className="text-center mt-4">
                  {billing?.state && lead?.state && (
                    <div className="text-muted mt-2 text-right small">
                      Billing State: <strong>{billing.state}</strong> | Lead State:{" "}
                      <strong>{lead.state}</strong> —{" "}
                      {billing.state.toLowerCase() === (lead.state || "").toLowerCase()
                        ? "CGST + SGST will apply"
                        : "IGST will apply"}
                    </div>
                  )}

                  <Button
                    className="btn btn-primary px-5 mt-3"
                    disabled={isSubmitting || missingLeadFields.state}
                    onClick={handleSubmitInvoice}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2" />
                        Updating...
                      </>
                    ) : (
                      <>Update Invoice</>
                    )}
                  </Button>
                </div>
              </div>

              {tiHeader?.ti_no && (
                <div className="mt-3 text-muted small">
                  TI No - <strong>{tiHeader.ti_no}</strong>
                  {tiHeader?.reference_pi_no && (
                    <>
                      {" "} | Against PI - <strong>{tiHeader.reference_pi_no}</strong>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditTIItems;
